// src/theme/AppThemeProvider.jsx
import React, { useCallback, useState, useMemo, useSyncExternalStore } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { getDesignTokens } from '@theme/getDesignTokens.js';
import { buildCustomizations } from '@theme/Customizations';
import { ColorModeContext } from '@theme/themePrimitives.js';
import { readColorPreference, resolveColorMode, writeColorPreference } from '@theme/colorMode.js';

const DARK_QUERY = '(prefers-color-scheme: dark)';
const deviceQuery = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DARK_QUERY) : null);
const subscribeToDevice = (onChange) => {
  const query = deviceQuery();
  query?.addEventListener?.('change', onChange);
  return () => query?.removeEventListener?.('change', onChange);
};
const deviceIsDark = () => Boolean(deviceQuery()?.matches);

/**
 * The app's theme, from the person's choice: light, dark, or the device's
 * (Profile → Appearance). "Match device" follows the OS live, so a sunset
 * switch flips the app with it. `toggleColorMode` stays for the sign-in pages'
 * one-click toggle, and flips light/dark from whatever shows now.
 */
export default function AppThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => readColorPreference(window.localStorage));
  const prefersDark = useSyncExternalStore(subscribeToDevice, deviceIsDark, () => false);
  const mode = resolveColorMode(preference, prefersDark);

  const setPreference = useCallback((next) => {
    writeColorPreference(window.localStorage, next);
    setPreferenceState(next);
  }, []);

  const toggleColorMode = useCallback(() => {
    setPreference(mode === 'light' ? 'dark' : 'light');
  }, [mode, setPreference]);

  const theme = useMemo(() => {
    const base = createTheme(getDesignTokens(mode));
    base.components = {
      ...base.components,
      ...buildCustomizations(base),
    };
    return base;
  }, [mode]);

  const colorModeValue = useMemo(
    () => ({ mode, preference, setPreference, toggleColorMode }),
    [mode, preference, setPreference, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={colorModeValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={(muiTheme) => ({
            body: {
              backgroundImage:
                'radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
              backgroundSize: 'cover',
              ...muiTheme.applyStyles('dark', {
                backgroundImage:
                  'radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
              }),
            },
          })}
        />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
